import React, { useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { SketchPicker } from 'react-color';
import type { PresetColor } from 'react-color/lib/components/sketch/Sketch';
import colorObjToString from '@utils/colorObjToString';
import colorStringToObj from '@utils/colorStringToObj';
import useToggle from '@hooks/useToggle';
import colorStringToHexAndAlpha from '@utils/colorStringToHexAndAlpha';
import { useColorPickerContext } from './context';

interface Props {
  className?: string;
  disableAlpha?: boolean;
  placeholder?: string;
  presetColors?: PresetColor[];
}

type Pos = {
  right: number;
  top?: number;
  bottom?: number;
};

function Bar({
  className,
  disableAlpha,
  placeholder = '',
  presetColors,
}: Props) {
  const { colorString, onChange } = useColorPickerContext();
  const nodeRef = useRef<HTMLDivElement>(null);
  const picker = useToggle(false);
  const [pos, setPos] = useState<Pos>({ right: 0, top: 0 });
  const { color, hex } = useMemo(
    () => ({
      color: colorStringToObj(colorString),
      hex: colorStringToHexAndAlpha(colorString).hex,
    }),
    [colorString]
  );

  const handleOpen = () => {
    const node = nodeRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    if (window.innerHeight - rect.bottom < 300) {
      setPos({
        right: window.innerWidth - rect.right,
        bottom: window.innerHeight - rect.top,
      });
    } else {
      setPos({
        right: window.innerWidth - rect.right,
        top: rect.bottom,
      });
    }
    picker.on();
  };

  return (
    <div className={classNames('relative', className)}>
      <div
        className="flex h-30 w-full cursor-pointer items-center rounded border border-dark bg-black px-10"
        ref={nodeRef}
        onClick={handleOpen}
      >
        {colorString ? (
          <>
            <div
              className="h-16 w-16"
              style={{ backgroundColor: `rgba(${colorString})` }}
            />
            <div className="ml-10">{hex}</div>
          </>
        ) : (
          <div className="text-black-gray">{placeholder}</div>
        )}
      </div>
      {picker.isOn &&
        ReactDOM.createPortal(
          <div>
            <div className="fixed inset-0" onClick={picker.off} />
            <div className="absolute w-220" style={pos}>
              <SketchPicker
                width="auto"
                disableAlpha={disableAlpha}
                className="text-black"
                color={color}
                presetColors={presetColors}
                onChange={(x) => onChange(colorObjToString(x.rgb))}
                onChangeComplete={(x) => {
                  onChange(colorObjToString(x.rgb));
                }}
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default Bar;
